import React from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {useNotifications} from "context/NotificationContext";
import {FormikHelpers} from "formik";
import {invalidate} from "services/query-client";
import Page from "components/Page";
import {ArticleData} from "types/Article";
import ArticleForm from '../Form';

function MutateArticle() {
    const {id} = useParams()
    const {data: article, isLoading} = useQuery(
        ['articles', {id}],
        () => ngage.articles.get(parseInt(id as string)),
        {enabled: !!id}
    )

    const store = useMutation((data: ArticleData) => ngage.articles.store(data))
    const update = useMutation((data: ArticleData) => ngage.articles.update(parseInt(id as string), data))

    const {notify} = useNotifications()
    const navigate = useNavigate()

    // Handle form submission
    async function handleSubmit(values: ArticleData, {setSubmitting}: FormikHelpers<ArticleData>) {
        id? await handleUpdate(values) : await handleStore(values)
        await invalidate('articles.all')

        setSubmitting(false)
        navigate('/articles')
    }

    // Handle store mutation
    async function handleStore(values: ArticleData) {
        try {
            await store.mutateAsync(values)
            notify("success", "Article Created", `${values.title} has been created`)
        } catch (error) {
            notify("error", "Article Not Created", `${values.title} could not be stored`)
            throw error
        }
    }

    // Handle update mutation
    async function handleUpdate(values: ArticleData) {
        try {
            await update.mutateAsync(values)
            notify("success", "Article Updated", `${values.title} has been updated`)
            await invalidate(['articles', {id}])
        } catch (error) {
            notify("error", "Article Not Updated", `${values.title} could not be updated`)
            throw error
        }
    }

    // Prepare breadcrumbs
    const newBreadcrumbs = {'App Content': '/', 'Featured': '/', 'News': '/articles', 'New Article': '/articles/new'}
    const editBreadcrumbs = {'App Content': '/', 'Featured': '/', 'News': '/articles', 'Edit Article': `/articles/${id}/edit`}

    return (
        <Page title={id ? "Edit Article" : "New Article"}
              breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
              isLoading={isLoading}>
            <ArticleForm article={article} onSubmit={handleSubmit}/>
        </Page>
    );
}

export default MutateArticle;